<template>
  <div class="cig-search">
    <el-form
      ref="cigForm"
      class="search-form"
      :model="model"
      :rules="rules"
      :status-icon="false"
      :hide-required-asterisk="true"
      label-position="top"
      v-bind="option"
    >
      <template v-for="(item, index) in data" :key="item.prop">
        <Transition name="el-zoom-in-top">
          <el-form-item
            v-show="isExpansion ? true : index < (max || 4)"
            class="search-item"
            v-bind="item"
            :label-width="option.labelWidth || item.labelWidth || '80px'"
            :style="{
              width: option.width || item.width || `${100 / (line || 4)}%`,
              order: item.order || index,
            }"
          >
            <el-input
              v-if="item.type === 'input'"
              v-model.trim="model[item.prop]"
              :type="item.type"
              v-bind="item.attribute"
              class="item-input"
            />
            <el-select
              v-else-if="item.type === 'select'"
              v-model="model[item.prop]"
              v-bind="item.attribute"
              class="item-input"
            >
              <el-option
                v-for="itx in item.options"
                :key="!isUndefined(itx.value) ? itx.value : itx"
                :label="!isUndefined(itx.label) ? itx.label : itx"
                :value="!isUndefined(itx.value) ? itx.value : itx"
              />
            </el-select>
            <el-date-picker
              v-else-if="dateType.includes(item.type)"
              v-model="model[item.prop]"
              :type="item.type"
              v-bind="item.attribute"
              class="item-input"
            />
            <el-cascader
              v-else-if="item.type === 'cascader'"
              v-model="model[item.prop]"
              :options="item.options"
              v-bind="item.attribute"
              class="item-input"
            />
            <template v-else-if="item.type === 'custom'">
              <slot :name="item.prop" :value="model" :option="item"></slot>
            </template>
          </el-form-item>
        </Transition>
      </template>

      <slot />
    </el-form>
    <!-- single 奇数情况下展示 -->
    <div :class="['search-button']">
      <el-button type="primary" :icon="IconSearch" @click="search">{{
        t('el.cigSearch.search')
      }}</el-button>
      <el-button :icon="IconReset" @click="reset">{{
        t('el.cigSearch.reset')
      }}</el-button>
      <el-button v-if="data!.length > (max || 4)" @click="changeExpansion"
        >{{ expansionText }}
        <el-icon style="padding-left: 8px" :size="14"
          ><component :is="expansionIcon" /></el-icon
      ></el-button>
    </div>
  </div>
</template>
<script lang="ts">
import {
  computed,
  defineComponent,
  onBeforeMount,
  reactive,
  ref,
  watch,
} from 'vue'
import { isUndefined } from '@park-ui/utils'
import { ElForm, ElFormItem } from '@park-ui/components/form'
import { ElInput } from '@park-ui/components/input'
import { ElOption, ElSelect } from '@park-ui/components/select'
import { ElDatePicker } from '@park-ui/components/date-picker'
import { ElCascader } from '@park-ui/components/cascader'
import { ElButton } from '@park-ui/components/button'
import ElIcon from '@park-ui/components/icon'
import { useLocale } from '@park-ui/hooks'
import {
  IconCaretDown,
  IconCaretUp,
  IconReset,
  IconSearch,
} from '@park-ui/components/cig-icon'
import defaultProps, { dateType } from './form/defaults'
import type { IDatePickerType } from './form/defaults'
export default defineComponent({
  name: 'ElCigSearch',
  components: {
    ElForm,
    ElFormItem,
    ElInput,
    ElSelect,
    ElOption,
    ElDatePicker,
    ElCascader,
    ElButton,
    ElIcon,
    IconCaretDown,
    IconCaretUp,
  },
  props: defaultProps,
  emits: ['search', 'reset'],
  setup(props, { emit }) {
    // init here
    const { t } = useLocale()
    const model = props.option?.model ? props.option.model : reactive({}) // 默认数据
    const rules = props.option?.rules ? props.option.rules : reactive({}) // 默认校验规则
    const cigForm = ref<typeof ElForm>() // 组件实例
    const width = computed(() => {
      return props.data!.length % 4 !== 0 ? 'auto' : '100%'
    })

    const formatValue = (type: IDatePickerType) => {
      let format = ''
      switch (type) {
        case 'year':
          format = 'YYYY'
          break
        case 'month':
          format = 'YYYY-MM'
          break
        case 'date':
          format = 'YYYY-MM-DD'
          break
        case 'dates':
          format = 'YYYY-MM-DD'
          break
        case 'week':
          format = 'YYYY-MM-DD [第]w[周]'
          break
        case 'datetimerange':
          format = 'YYYY-MM-DD HH:mm:ss'
          break
        case 'daterange':
          format = 'YYYY-MM-DD'
          break
        case 'monthrange':
          format = 'YYYY-MM'
          break
        default:
          format = 'YYYY-MM-DD HH:mm:ss'
          break
      }
      return format
    }
    const initRule = (item: any) => {
      if (item.rules) return item.rules
      let rules: any[] = []
      switch (item.type) {
        case 'input':
          rules = [
            {
              required: true,
              message: item.attribute?.placeholder || '请输入',
              trigger: 'blur',
            },
          ]
          break
        case 'select':
          rules = [
            {
              required: true,
              message: item.attribute?.placeholder || '请选择',
              trigger: 'change',
            },
          ]
          break
        case 'cascader':
          rules = [
            {
              required: true,
              message: item.attribute?.placeholder || '请选择',
              trigger: 'change',
            },
          ]
          break
        case 'datetimerange':
        case 'daterange':
        case 'monthrange':
          rules = [
            {
              type: 'array',
              required: true,
              message: item.attribute?.placeholder || '请选择',
              trigger: 'change',
            },
          ]
          break
        default:
          rules = [
            {
              type: 'date',
              required: true,
              message: item.attribute?.placeholder || '请选择',
              trigger: 'change',
            },
          ]

          break
      }
      return rules
    }
    // 搜索
    const search = () => {
      cigForm.value!.validate((boolean: any) => {
        if (boolean) {
          emit('search', model)
        }
      })
    }
    // 重置
    const reset = () => {
      cigForm.value!.resetFields()
      emit('reset', model)
    }
    onBeforeMount(() => {
      // 帮助外部初始化时间选择器format必须在挂载前完成
      props.data?.forEach((x: any) => {
        if (!props.option.model) model[x.prop] = x.defaultValue
        if (!props.option.rules && x.required) rules[x.prop] = initRule(x)
        if (!x.attribute) x.attribute = {}
        if (dateType.includes(x.type)) {
          if (!x.attribute.format) x.attribute.format = formatValue(x.type)
          if (!x.attribute.valueFormat)
            x.attribute.valueFormat = formatValue(x.type)
        }
        if (!x.attribute.placeholder) {
          if (x.type === 'input') {
            x.attribute.placeholder = t('el.cigSearch.inputPlaceholder')
          } else {
            x.attribute.placeholder = t('el.cigSearch.selectPlaceholder')
          }
          if (x.label) x.attribute.placeholder += x.label
        }
      })
    })
    // 高级搜索
    const isExpansion = ref(false)
    function changeExpansion() {
      isExpansion.value = !isExpansion.value
    }
    const expansionIcon = computed(() =>
      isExpansion.value ? 'IconCaretUp' : 'IconCaretDown'
    )
    const expansionText = computed(() => {
      const i18nText = !isExpansion.value
        ? 'el.cigSearch.expansion'
        : 'el.cigSearch.collapse'
      return t(i18nText)
    })
    const line = computed(() => props.option.line)
    const max = computed(() => props.option.max)
    watch(
      () => props.data,
      () => {
        if (props.data && props.data!.length < 4) {
          props.option!.line = props.data!.length
        } else {
          props.option!.line = 4
        }
      },
      {
        immediate: true,
      }
    )
    return {
      t,
      model,
      rules,
      cigForm,
      dateType,
      width,
      isExpansion,
      expansionIcon,
      expansionText,
      isUndefined,
      formatValue,
      search,
      reset,
      changeExpansion,
      IconSearch,
      IconReset,
      line,
      max,
    }
  },
})
</script>
